import { useState } from "react";
import { imgSrc } from "@/lib/images";

// Uniform "blur-fill" card art. A blurred, zoomed copy of the image fills the
// tile behind a centred, uncropped `object-contain` foreground — so mixed aspect
// ratios always look tidy and nothing is awkwardly cropped. Used across every
// listing grid (Kathas, Mantras, Temples, Deities, eBooks…).
//
// Every image is served through the backend resize+cache proxy (small, same-origin,
// permanently cached WebP) so cards stay reliable on throttled mobile networks.
// If the source 404s (e.g. a deleted upload) the tile self-heals to the `fallback`
// node instead of showing a broken/blank box.
export default function CardImage({
  src,
  alt = "",
  onError,
  ratio = "aspect-[4/3]",
  rounded = "",
  className = "",
  fallback = null,
  width = 640,
}) {
  const [failed, setFailed] = useState(false);
  // One request per card: the same optimized image is reused for the blurred
  // backdrop and the foreground, so the browser fetches it once (and both are
  // lazy) — avoids saturating the connection limit and leaving cards blank.
  const fg = imgSrc(src, width);
  const handleError = (e) => {
    if (onError) onError(e);
    // If the caller's onError swapped in a working fallback src, don't blank out.
    // Only mark failed when the element is still pointing at the original.
    if (!onError) setFailed(true);
  };
  return (
    <div className={`relative overflow-hidden bg-[var(--surface-alt)] ${ratio} ${rounded} ${className}`}>
      {fg && !failed ? (
        <>
          <img
            src={fg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
          />
          <img
            src={fg}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={handleError}
            className="absolute inset-0 mx-auto h-full w-auto max-w-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[var(--surface-alt)] to-[var(--parchment)]">
          {fallback || <span className="font-deva text-3xl text-[var(--gold)]/40" aria-hidden="true">ॐ</span>}
        </div>
      )}
    </div>
  );
}
